import React, { ReactNode } from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { frameSx, hexToRgba } from '../theme/tokens';

const CORNER = 14;

/** One right-angle bracket. Drawn as two borders on an absolutely-positioned
 * square rather than as a pseudo-element, so the accent color can be passed
 * per instance. */
const cornerSx = (color: string, top: boolean, left: boolean) => ({
  position: 'absolute' as const,
  width: CORNER,
  height: CORNER,
  pointerEvents: 'none' as const,
  [top ? 'top' : 'bottom']: -1,
  [left ? 'left' : 'right']: -1,
  [top ? 'borderTop' : 'borderBottom']: `2px solid ${color}`,
  [left ? 'borderLeft' : 'borderRight']: `2px solid ${color}`,
  [top ? (left ? 'borderTopLeftRadius' : 'borderTopRightRadius') : left ? 'borderBottomLeftRadius' : 'borderBottomRightRadius']: '10px',
});

/**
 * The universal panel — a translucent surface over the void with a
 * wormhole-tinted hairline, plus the four corner brackets that are this
 * world's signature idiom (adventure's "targeting reticle", the shape a
 * piece of live-readout content gets framed in).
 *
 * v11 stripped the brackets out to copy EVE's flat client chrome; v12
 * replaced them with a colour-blocked LCARS top rail. Both readings are
 * retired — the bracket is what the corp's own app uses, and it is static,
 * so the treatment reads in a still frame rather than only mid-animation.
 *
 * `corners={false}` genuinely turns them off now (it was accepted and ignored
 * for two passes), for the dense nested rows where four brackets per row
 * would be noise.
 */
const ScanFrame: React.FC<{ color: string; children: ReactNode; sx?: SxProps<Theme>; corners?: boolean }> = ({
  color,
  children,
  sx,
  corners = true,
}) => (
  <Box sx={{ ...frameSx(color), display: 'block', position: 'relative', px: { xs: 2, md: 2.5 }, py: { xs: 1.5, md: 2 }, ...sx }}>
    {corners && (
      <>
        <Box sx={cornerSx(hexToRgba(color, 0.55), true, true)} />
        <Box sx={cornerSx(hexToRgba(color, 0.55), true, false)} />
        <Box sx={cornerSx(hexToRgba(color, 0.55), false, true)} />
        <Box sx={cornerSx(hexToRgba(color, 0.55), false, false)} />
      </>
    )}
    {children}
  </Box>
);

export default ScanFrame;
